import { getStorage, ref as storageRef, uploadString } from "firebase/storage";
import { firebaseApp } from "../../firebase/firebaseApp";
import { saveUserClinicMapping } from "./saveUserClinicMapping";
import { metadataFilePath } from "../patient/patientStoragePath";

/**
 * @typedef {object} UploadMetadataOptions
 * @property {string|null|undefined} [id]
 */

/**
 * Persist a form-answers object as a JSON file in Cloud Storage.
 *
 * Path rules:
 * - If no id (blank/undefined): user_metadata/{uid}/{file}
 * - If id present:           user_metadata/{uid}/{id}/{file}
 *
 * Filename format: 2025-06-26T17-43-10Z-n36kf.json
 *
 * @param {string} uid                 Firebase Auth UID
 * @param {Object<string, *>} answers Key⇢value map of answers
 * @param {UploadMetadataOptions} [opts]
 * @returns {Promise<string>}          The storage ref string that was written
 */
export async function uploadMetadata(uid, answers, opts) {
  if (!uid) throw new Error("uploadMetadata: uid missing");
  if (typeof answers !== "object" || answers === null) {
    throw new Error("uploadMetadata: answers must be an object");
  }

  // Patient context: explicit opt wins, otherwise fall back to answers.id
  const id = (opts && "id" in opts ? opts.id : answers?.id) ?? "";

  // Build filename
  const iso = new Date().toISOString().replace(/:/g, "-");
  const rand = Math.random().toString(36).slice(2, 7);
  const fileName = `${iso}-${rand}.json`;

  // Upload
  const storage = getStorage(firebaseApp);
  const objectPath = metadataFilePath(uid, id, fileName);
  const fileRef = storageRef(storage, objectPath);

  await uploadString(fileRef, JSON.stringify(answers), "raw", {
    contentType: "application/json",
  });

  await saveUserClinicMapping(uid, {
    id: answers?.id,
    clinicStudyId: answers?.clinicStudyId,
  });

  return fileRef.toString();
}
